// Abrir un video en el Estudio de Video, con sus avisos — la mitad compartida de
// `PanelEditarConEstudio` (la barra del lienzo) y de la tarjeta `ir_al_estudio` del chat
// (ADR-2026-10-11-ir-al-estudio-desde-el-chat). Quién navega después es cosa de cada
// uno: el panel va al editor a secas (o con el state de Redes), la tarjeta pasa por
// `llegarAlEstudio` del chat para seguir la conversación ahí.
//
// Mismo gating que siempre: el plan (Pro y Ultra; dev testers pasan), de escritorio, y
// la primera vez se explica con `StudioIntroModal`. `dev_tester` y `plan` se leen de la
// MISMA fila y en la misma consulta, no de `useUserGroup`: el hook vale `null` en el
// primer login sin cache, y como esto corre una sola vez, "mientras carga no se bloquea"
// (lo que hacen Modos y Creaciones con una card o un botón que se vuelven a evaluar) acá
// dejaba pasar a un plan que no llega para siempre — y ni la creación del proyecto ni
// `/editor-video/:id` vuelven a mirar el plan. Si la consulta falla o no trae plan, NO
// se sigue: con `null`, `getModePlanLock` no bloquea nada, y acá eso sería abrir el
// Estudio sin saber si el plan llega.

import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';
import { DesktopOnlyModal } from '../../../components/DesktopOnlyModal';
import { FeatureLockedModal } from '../../../components/FeatureLockedModal';
import { StudioIntroModal } from '../../../components/StudioIntroModal';
import { isSeen as isOnboardingSeen, loadOnboardingSeen, markSeen as markOnboardingSeen } from '../../../lib/onboardingSeen';
import { getModePlanLock, type ModePlanLock } from '../../../lib/planCapabilities';
import { supabase } from '../../../lib/supabase';
import { createStudioProjectFromVideo, type CreateStudioProjectFromVideoParams } from '../../../utils/videoProject';

/** Qué video abrir: lo que `createStudioProjectFromVideo` necesita además de la empresa. */
export type VideoParaElEstudio = Omit<CreateStudioProjectFromVideoParams, 'companyId' | 'userId'>;

/** Por qué NO se abrió: el plan no llega, es un teléfono, cerró el aviso, o falló. */
export type MotivoDeNoAbrir = 'plan' | 'solo-compu' | 'cerrado' | 'error';

export type PasoDeAbrirEnEstudio =
  | { tipo: 'inactivo' }
  | { tipo: 'revisando' }
  | { tipo: 'bloqueado'; lock: ModePlanLock }
  | { tipo: 'solo-compu' }
  | { tipo: 'intro'; userId: string | null; video: VideoParaElEstudio }
  | { tipo: 'creando' };

export function useAbrirVideoEnEstudio(args: {
  companyId: string;
  /** El proyecto ya existe con el video en la timeline: el que llama navega. */
  onAbierto: (projectId: string) => void;
  /** No se abrió. `mensaje` viene sólo con 'error' (para mostrarlo). */
  onNoAbierto: (motivo: MotivoDeNoAbrir, mensaje?: string) => void;
}) {
  const [paso, setPaso] = useState<PasoDeAbrirEnEstudio>({ tipo: 'inactivo' });
  // Por ref: los callbacks del que llama cambian en cada render y acá no hace falta
  // rehacer nada por eso.
  const argsRef = useRef(args);
  argsRef.current = args;
  const pasoRef = useRef(paso);
  pasoRef.current = paso;

  const crear = useCallback(async (userId: string | null, video: VideoParaElEstudio) => {
    setPaso({ tipo: 'creando' });
    try {
      const projectId = await createStudioProjectFromVideo({
        companyId: argsRef.current.companyId,
        userId,
        ...video,
      });
      setPaso({ tipo: 'inactivo' });
      argsRef.current.onAbierto(projectId);
    } catch (err) {
      console.error('createStudioProjectFromVideo failed', err);
      setPaso({ tipo: 'inactivo' });
      argsRef.current.onNoAbierto('error', 'No se pudo crear el proyecto en el Estudio. Probá de nuevo en un momento.');
    }
  }, []);

  const abrir = useCallback(
    async (video: VideoParaElEstudio) => {
      setPaso({ tipo: 'revisando' });
      const { data, error } = await supabase
        .from('company_profiles')
        .select('plan, dev_tester')
        .eq('id_company', argsRef.current.companyId)
        .maybeSingle();
      const fila = data as { plan?: string | null; dev_tester?: boolean | null } | null;
      if (error || !fila || (fila.dev_tester !== true && !fila.plan)) {
        setPaso({ tipo: 'inactivo' });
        argsRef.current.onNoAbierto('error', 'No pudimos revisar tu plan. Probá de nuevo en un momento.');
        return;
      }
      if (fila.dev_tester !== true) {
        const lock = getModePlanLock('editor-video', fila.plan);
        if (lock) {
          setPaso({ tipo: 'bloqueado', lock });
          return;
        }
      }
      if (window.matchMedia('(max-width: 767px)').matches) {
        setPaso({ tipo: 'solo-compu' });
        return;
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const uid = user?.id ?? null;
      if (uid) await loadOnboardingSeen(uid);
      if (!isOnboardingSeen('estudio_from_historial')) {
        setPaso({ tipo: 'intro', userId: uid, video });
        return;
      }
      void crear(uid, video);
    },
    [crear],
  );

  /** Cerró un aviso (plan, teléfono o la intro) sin seguir. */
  const cerrarAviso = useCallback(() => {
    const p = pasoRef.current;
    setPaso({ tipo: 'inactivo' });
    argsRef.current.onNoAbierto(p.tipo === 'bloqueado' ? 'plan' : p.tipo === 'solo-compu' ? 'solo-compu' : 'cerrado');
  }, []);

  /** Aceptó la intro: queda vista y se crea el proyecto. */
  const confirmarIntro = useCallback(() => {
    const p = pasoRef.current;
    if (p.tipo !== 'intro') return;
    markOnboardingSeen('estudio_from_historial', p.userId);
    void crear(p.userId, p.video);
  }, [crear]);

  return { paso, abrir, cerrarAviso, confirmarIntro };
}

/** Los avisos del hook: el modal de plan, el de "sólo en compu", la intro del Estudio y
 *  el cartel mientras se copia el video. Se dibujan donde se use el hook. */
export const AvisosDeAbrirEnEstudio: React.FC<{
  paso: PasoDeAbrirEnEstudio;
  onCerrar: () => void;
  onConfirmarIntro: () => void;
}> = ({ paso, onCerrar, onConfirmarIntro }) => (
  <>
    <FeatureLockedModal
      open={paso.tipo === 'bloqueado'}
      onClose={onCerrar}
      title={paso.tipo === 'bloqueado' ? paso.lock.title : ''}
      message={paso.tipo === 'bloqueado' ? paso.lock.message : ''}
    />
    <DesktopOnlyModal open={paso.tipo === 'solo-compu'} onClose={onCerrar} />
    <StudioIntroModal open={paso.tipo === 'intro'} onClose={onCerrar} onConfirm={onConfirmarIntro} />
    {paso.tipo === 'creando' &&
      createPortal(
        <div className="fixed bottom-6 left-1/2 z-[250] -translate-x-1/2 px-4" role="status">
          <span className="flex items-center gap-2 rounded-2xl bg-brand-black py-3 px-5 text-sm text-white shadow-xl">
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            Abriendo el video en el Estudio…
          </span>
        </div>,
        document.body,
      )}
  </>
);
